// Pages légales publiques : Mentions légales, Politique de confidentialité, CGU/CGV.
// Accessibles sans authentification. Mise en page sobre (palette AERH), responsive.
// Hébergement : Google Cloud Platform via Emergent (hors UE).
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const LOGO = "/logo_GAP.jpg";

// --- Composants de mise en page réutilisables ---
const Doc = ({ titre, sousTitre, children }) => (
  <div data-testid="page-legale" className="min-h-screen bg-white text-[#1D3D3D] flex flex-col">
    <header className="border-b border-[#1D3D3D]/10 sticky top-0 bg-white/95 backdrop-blur-sm z-10">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="retour-accueil-link" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1D3D3D] hover:text-[#4A8A8A] transition-colors">
          <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
        </Link>
        <Link to="/" className="flex items-center gap-2">
          <img src={LOGO} alt="Groupe AERH Polynésie — Porinetia Compta AERH" className="h-8 w-auto rounded" />
        </Link>
      </div>
    </header>
    <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
      <h1 className="text-3xl sm:text-4xl font-black tracking-tight">{titre}</h1>
      {sousTitre && <p className="mt-2 text-sm font-medium text-[#4A8A8A]">{sousTitre}</p>}
      <div className="mt-8 space-y-7 text-base leading-relaxed">{children}</div>
    </main>
    <footer className="border-t border-[#1D3D3D]/10">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-[#1D3D3D]/60">
        <Link to="/mentions-legales" className="hover:text-[#1D3D3D]">Mentions légales</Link>
        <Link to="/politique-confidentialite" className="hover:text-[#1D3D3D]">Politique de confidentialité</Link>
        <Link to="/cgu" className="hover:text-[#1D3D3D]">CGU/CGV</Link>
        <span className="ml-auto">© 2026 Groupe AERH Polynésie</span>
      </div>
    </footer>
  </div>
);

const Art = ({ titre, children }) => (
  <section>
    <h2 className="text-xl font-bold tracking-tight text-[#1D3D3D] border-b border-[#4A8A8A]/20 pb-2 mb-3">{titre}</h2>
    <div className="space-y-3 text-[#1D3D3D]/85">{children}</div>
  </section>
);

const Sub = ({ titre, children }) => (
  <div>
    <h3 className="text-base font-semibold text-[#2D5A5A] mt-4 mb-1.5">{titre}</h3>
    <div className="space-y-2 text-[#1D3D3D]/85">{children}</div>
  </div>
);

const UL = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1.5 text-[#1D3D3D]/85">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);

const Coords = () => (
  <p>
    SARL Groupe AERH Polynésie<br />
    10 Rue Georges LAGARDE — Papeete, Polynésie française<br />
    Email : <a href="mailto:contact@groupeaerhpolynesie.com" className="text-[#4A8A8A] font-medium">contact@groupeaerhpolynesie.com</a> — Téléphone : 87 73 78 15
  </p>
);

// ======================= MENTIONS LÉGALES =======================
export function MentionsLegales() {
  return (
    <Doc titre="Mentions légales" sousTitre="Porinetia Compta AERH — Version 1.0 — Juin 2026">
      <Art titre="Éditeur du service">
        <UL items={[
          <>Raison sociale : <strong>SARL Groupe AERH Polynésie</strong></>,
          "Siège social : 10 Rue Georges LAGARDE, Papeete, Polynésie française",
          "N° Tahiti : F80302",
          "RCS Papeete : 24 112B",
          "Gérant : Eric ASPINAS",
          <>Email : <a href="mailto:contact@groupeaerhpolynesie.com" className="text-[#4A8A8A]">contact@groupeaerhpolynesie.com</a></>,
          "Téléphone : 87 73 78 15",
          <>Site web : <a href="https://porinetia-compta.com" className="text-[#4A8A8A]">https://porinetia-compta.com</a></>,
        ]} />
      </Art>
      <Art titre="Hébergement">
        <p>Le service Porinetia Compta AERH est hébergé par la plateforme Emergent (<a href="https://emergent.sh" className="text-[#4A8A8A]">https://emergent.sh</a>), dont les applications sont déployées sur l'infrastructure Google Cloud Platform (GCP). Les données sont hébergées en dehors de l'Union Européenne. La Polynésie française n'étant pas membre de l'Union Européenne, le RGPD européen ne s'applique pas directement aux traitements effectués dans ce cadre.</p>
      </Art>
      <Art titre="Directeur de la publication">
        <p>Eric ASPINAS, Gérant de la SARL Groupe AERH Polynésie.</p>
      </Art>
      <Art titre="Propriété intellectuelle">
        <p>L'ensemble du contenu du service Porinetia Compta AERH (interface, code source, textes, graphismes, logo) est la propriété exclusive de la SARL Groupe AERH Polynésie et est protégé par le droit applicable en Polynésie française. Toute reproduction, représentation, modification ou exploitation non autorisée de tout ou partie du service est strictement interdite.</p>
      </Art>
      <Art titre="Données personnelles">
        <p>Le traitement des données personnelles des Utilisateurs est décrit dans la Politique de confidentialité disponible sur le service. Conformément aux dispositions applicables, l'Utilisateur dispose d'un droit d'accès, de rectification et de suppression de ses données personnelles en contactant AERH à l'adresse contact@groupeaerhpolynesie.com.</p>
      </Art>
      <Art titre="Cookies">
        <p>Le service Porinetia Compta AERH utilise des cookies techniques strictement nécessaires au fonctionnement du service (authentification, session). Aucun cookie publicitaire ou de traçage tiers n'est utilisé.</p>
      </Art>
      <Art titre="Paiement en ligne">
        <p>Les paiements en ligne sont traités par Stripe, Inc. AERH ne conserve aucune donnée bancaire des Utilisateurs. Stripe est certifié PCI-DSS niveau 1. Pour toute question relative à la facturation : contact@groupeaerhpolynesie.com</p>
      </Art>
      <Art titre="Responsabilité">
        <p>AERH met tout en œuvre pour assurer la disponibilité et la sécurité du service Porinetia Compta AERH. Toutefois, AERH ne saurait être tenu responsable des interruptions de service, pertes de données ou dommages résultant d'événements hors de son contrôle. L'Utilisateur est seul responsable de l'exactitude des données qu'il saisit dans le service et du respect de ses obligations fiscales et comptables en Polynésie française.</p>
      </Art>
      <Art titre="Droit applicable">
        <p>Les présentes mentions légales sont soumises au droit applicable en Polynésie française. Tout litige sera soumis à la compétence exclusive des juridictions de Papeete.</p>
      </Art>
    </Doc>
  );
}

// ================= POLITIQUE DE CONFIDENTIALITÉ =================
export function PolitiqueConfidentialite() {
  return (
    <Doc titre="Politique de confidentialité" sousTitre="Porinetia Compta AERH — Groupe AERH Polynésie — Version 1.0 — Juin 2026">
      <Art titre="Article 1 — Responsable du traitement">
        <p>Le responsable du traitement des données personnelles collectées via le service Porinetia Compta AERH est :</p>
        <p>SARL Groupe AERH Polynésie<br />10 Rue Georges LAGARDE — Papeete, Polynésie française<br />N° Tahiti : F80302 — RCS : 24 112B<br />Contact : contact@groupeaerhpolynesie.com — 87 73 78 15</p>
      </Art>
      <Art titre="Article 2 — Données collectées">
        <Sub titre="2.1 Données d'identification et de compte">
          <UL items={["Nom, prénom du gérant ou responsable", "Adresse email de connexion", "Mot de passe (stocké sous forme hachée — jamais en clair)", "Numéro de téléphone"]} />
        </Sub>
        <Sub titre="2.2 Données de l'entreprise">
          <UL items={["Raison sociale, forme juridique, adresse", "Numéro Tahiti, numéro RCS, numéro employeur", "Régime TVA et secteur d'activité"]} />
        </Sub>
        <Sub titre="2.3 Données comptables">
          <UL items={["Écritures comptables saisies par l'Utilisateur", "Factures, tiers (clients et fournisseurs)", "Déclarations TVA et états financiers", "Ces données appartiennent exclusivement à l'Utilisateur. AERH n'y accède qu'à des fins de support technique, sur demande expresse de l'Utilisateur."]} />
        </Sub>
        <Sub titre="2.4 Données de paiement">
          <UL items={["Les données bancaires (numéro de carte, etc.) sont collectées et traitées exclusivement par Stripe, Inc. AERH n'y a pas accès. AERH reçoit uniquement une confirmation de paiement et un identifiant de transaction."]} />
        </Sub>
        <Sub titre="2.5 Données de connexion">
          <UL items={["Adresse IP", "Date et heure de connexion", "Type de navigateur et système d'exploitation", "Ces données sont collectées à des fins de sécurité et de détection des tentatives d'accès non autorisées."]} />
        </Sub>
      </Art>
      <Art titre="Article 3 — Finalités du traitement">
        <UL items={[
          "Fourniture du service Porinetia Compta AERH et gestion des Comptes utilisateurs.",
          "Gestion des abonnements et de la facturation.",
          "Envoi des emails transactionnels liés à l'utilisation du service (confirmation d'inscription, alertes de fin d'essai, confirmation de paiement, etc.).",
          "Support utilisateur et réponse aux demandes.",
          "Amélioration et sécurisation du service.",
          "Respect des obligations légales applicables en Polynésie française.",
        ]} />
        <p>AERH ne vend pas et ne cède pas les données personnelles des Utilisateurs à des tiers à des fins commerciales.</p>
      </Art>
      <Art titre="Article 4 — Durée de conservation">
        <UL items={[
          "Données de compte actif : conservées pendant toute la durée de l'abonnement.",
          "Données après résiliation : conservées pendant 12 mois, délai pendant lequel l'Utilisateur peut exporter ses données.",
          "Données comptables : conservées 10 ans après la clôture de l'exercice concerné, conformément aux obligations légales.",
          "Données de connexion : conservées 12 mois à des fins de sécurité.",
        ]} />
      </Art>
      <Art titre="Article 5 — Hébergement et sécurité">
        <p>Les données sont hébergées sur Google Cloud Platform (GCP) via la plateforme Emergent (<a href="https://emergent.sh" className="text-[#4A8A8A]">https://emergent.sh</a>), en dehors de l'Union Européenne. Google Cloud Platform est certifié ISO 27001 et SOC 2.</p>
        <p>Les mesures de sécurité mises en œuvre incluent :</p>
        <UL items={[
          "Chiffrement des données en transit (HTTPS/TLS)",
          "Chiffrement des données au repos (AES-256)",
          "Authentification par token JWT avec expiration automatique",
          "Protection anti-brute-force sur les tentatives de connexion",
          "Sauvegarde automatique quotidienne avec réplication",
          "Accès aux données restreint au personnel AERH habilité",
        ]} />
      </Art>
      <Art titre="Article 6 — Droits des Utilisateurs">
        <p>Conformément aux dispositions applicables en Polynésie française, l'Utilisateur dispose des droits suivants sur ses données personnelles :</p>
        <UL items={[
          "Droit d'accès : obtenir une copie des données personnelles le concernant.",
          "Droit de rectification : corriger des données inexactes ou incomplètes.",
          "Droit à l'effacement : demander la suppression de ses données, sous réserve des obligations légales de conservation.",
          "Droit à la portabilité : recevoir ses données dans un format structuré et lisible (export disponible depuis Paramètres).",
        ]} />
        <p>Pour exercer ces droits, l'Utilisateur peut contacter AERH à l'adresse contact@groupeaerhpolynesie.com. AERH s'engage à répondre dans un délai de 30 jours.</p>
      </Art>
      <Art titre="Article 7 — Sous-traitants">
        <p>AERH fait appel aux sous-traitants suivants pour la fourniture du service :</p>
        <UL items={[
          <>Stripe, Inc. (San Francisco, CA, USA) : traitement des paiements en ligne. Certifié PCI-DSS niveau 1. Politique de confidentialité : <a href="https://stripe.com/fr/privacy" className="text-[#4A8A8A]">https://stripe.com/fr/privacy</a></>,
          "Resend (service d'envoi d'emails transactionnels) : envoi des notifications par email.",
          <>Emergent (<a href="https://emergent.sh" className="text-[#4A8A8A]">https://emergent.sh</a>) / Google Cloud Platform : hébergement de l'application et des données. Infrastructure certifiée ISO 27001.</>,
        ]} />
        <p>Ces sous-traitants sont sélectionnés pour leur conformité aux standards de sécurité applicables.</p>
      </Art>
      <Art titre="Article 8 — Cookies">
        <p>Le service Porinetia Compta AERH utilise uniquement des cookies techniques strictement nécessaires :</p>
        <UL items={[
          "Cookie de session : maintient la connexion de l'Utilisateur (httpOnly, sécurisé, expiration 30 minutes d'inactivité).",
          "Token d'authentification JWT : identifie l'Utilisateur de manière sécurisée.",
        ]} />
        <p>Aucun cookie publicitaire, de profilage ou de traçage tiers n'est utilisé.</p>
      </Art>
      <Art titre="Article 9 — Modification de la politique">
        <p>AERH se réserve le droit de modifier la présente Politique de confidentialité. Toute modification substantielle sera notifiée à l'Utilisateur par email avec un préavis de 30 jours.</p>
      </Art>
      <Art titre="Article 10 — Contact">
        <p>Pour toute question relative à la présente Politique de confidentialité ou à l'exercice de vos droits :</p>
        <Coords />
      </Art>
    </Doc>
  );
}

// ===================== CGU / CGV =====================
export function Cgu() {
  return (
    <Doc titre="Conditions Générales d'Utilisation et de Vente" sousTitre="Porinetia Compta AERH — Groupe AERH Polynésie — Version 1.0 — Juin 2026 — Document soumis au droit polynésien">
      <Art titre="Article 1 — Objet et champ d'application">
        <p>Les présentes Conditions Générales d'Utilisation et de Vente (ci-après « CGU/CGV ») régissent l'accès et l'utilisation du service en ligne Porinetia Compta AERH (ci-après « le Service ») édité par la SARL Groupe AERH Polynésie, dont le siège social est situé 10 Rue Georges LAGARDE, Papeete, Polynésie française, immatriculée au RCS de Papeete sous le numéro 24 112B, N° Tahiti F80302 (ci-après « AERH »). Le Service est accessible à l'adresse https://porinetia-compta.com et constitue un logiciel de comptabilité en mode SaaS (Software as a Service) destiné aux entreprises de Polynésie française. L'accès au Service implique l'acceptation pleine et entière des présentes CGU/CGV. Toute utilisation du Service vaut acceptation sans réserve des présentes conditions.</p>
      </Art>
      <Art titre="Article 2 — Définitions">
        <UL items={[
          <>« Service » : la plateforme Porinetia Compta AERH accessible en ligne, incluant toutes ses fonctionnalités.</>,
          <>« Utilisateur » : toute personne physique ou morale accédant au Service dans le cadre de son activité professionnelle.</>,
          <>« Compte » : l'espace personnel de l'Utilisateur créé lors de l'inscription.</>,
          <>« Données » : l'ensemble des informations saisies par l'Utilisateur dans le Service.</>,
          <>« Abonnement » : le contrat d'accès payant au Service souscrit par l'Utilisateur.</>,
          <>« Période d'essai » : la période de 30 jours gratuits accordée à tout nouvel Utilisateur sans carte bancaire.</>,
        ]} />
      </Art>
      <Art titre="Article 3 — Accès au Service">
        <Sub titre="3.1 Inscription">
          <p>L'accès au Service nécessite la création d'un Compte. L'Utilisateur s'engage à fournir des informations exactes, complètes et à jour lors de l'inscription, notamment son Numéro Tahiti, ses coordonnées et son régime TVA. L'Utilisateur est seul responsable de la confidentialité de ses identifiants de connexion. Toute utilisation du Service avec ses identifiants est réputée effectuée par l'Utilisateur.</p>
        </Sub>
        <Sub titre="3.2 Questionnaire de positionnement">
          <p>Avant tout accès à la saisie comptable, l'Utilisateur doit compléter un questionnaire de positionnement obligatoire. En cas de score insuffisant (inférieur à 80%), une formation préalable de 3 heures est proposée à 60 000 F TTC. L'Utilisateur peut choisir d'accéder au Service sans formation, sous réserve d'accepter expressément une décharge de responsabilité quant à la qualité de ses saisies.</p>
        </Sub>
        <Sub titre="3.3 Période d'essai">
          <p>Tout nouvel Utilisateur bénéficie d'une période d'essai de 30 jours gratuits à compter de la création de son Compte, sans obligation de renseigner un moyen de paiement. À l'issue de cette période, la poursuite de l'accès au Service est conditionnée à la souscription d'un Abonnement payant.</p>
        </Sub>
      </Art>
      <Art titre="Article 4 — Abonnements et tarifs">
        <Sub titre="4.1 Formules d'abonnement">
          <p>Le Service est proposé en trois formules d'abonnement mensuel ou annuel :</p>
          <UL items={[
            "Solo : 3 500 XPF/mois — 1 utilisateur, destiné aux TPE de 1 à 5 personnes.",
            "Équipe : 6 500 XPF/mois — jusqu'à 5 utilisateurs, destiné aux PME de 6 à 20 personnes.",
            "Structure : 12 000 XPF/mois — utilisateurs illimités, destiné aux PME de 20 personnes et plus.",
          ]} />
          <p>Les abonnements annuels bénéficient d'une remise de 10% par rapport au tarif mensuel. Les tarifs s'entendent en francs CFP (XPF), toutes taxes comprises applicables.</p>
        </Sub>
        <Sub titre="4.2 Facturation et paiement">
          <p>Le paiement est effectué par carte bancaire via la plateforme sécurisée Stripe. AERH ne conserve aucune donnée bancaire de l'Utilisateur. L'Abonnement est renouvelé automatiquement à échéance, selon la périodicité choisie (mensuelle ou annuelle), sauf résiliation préalable.</p>
        </Sub>
        <Sub titre="4.3 Modification des tarifs">
          <p>AERH se réserve le droit de modifier ses tarifs. Toute modification tarifaire sera notifiée à l'Utilisateur par email avec un préavis minimum de 30 jours. L'Utilisateur pourra résilier son Abonnement avant l'entrée en vigueur du nouveau tarif.</p>
        </Sub>
        <Sub titre="4.4 Évolution de formule">
          <p>L'Utilisateur peut évoluer vers une formule supérieure à tout moment depuis son espace Paramètres. La différence est calculée au prorata par Stripe et facturée immédiatement. Le passage à une formule inférieure n'est pas possible en ligne — l'Utilisateur doit contacter AERH à l'adresse contact@groupeaerhpolynesie.com.</p>
        </Sub>
      </Art>
      <Art titre="Article 5 — Résiliation">
        <Sub titre="5.1 Résiliation par l'Utilisateur">
          <p>L'Utilisateur peut résilier son Abonnement à tout moment depuis son espace Paramètres, avec un préavis de 30 jours. L'accès au Service est maintenu jusqu'à la fin de la période payée en cours. Après résiliation effective, le Compte passe en mode lecture seule. Les données sont conservées pendant 12 mois après la résiliation effective, délai pendant lequel l'Utilisateur peut exporter ses données.</p>
        </Sub>
        <Sub titre="5.2 Résiliation par AERH">
          <p>AERH se réserve le droit de suspendre ou résilier l'accès au Service en cas de non-paiement, de violation des présentes CGU/CGV, ou d'utilisation frauduleuse, après mise en demeure restée sans effet pendant 15 jours.</p>
        </Sub>
      </Art>
      <Art titre="Article 6 — Obligations de l'Utilisateur">
        <p>L'Utilisateur s'engage à :</p>
        <UL items={[
          "Utiliser le Service uniquement dans le cadre de son activité professionnelle légale.",
          "Saisir des données exactes et conformes à la réalité comptable de son entreprise.",
          "Ne pas tenter de contourner les mesures de sécurité du Service.",
          "Ne pas partager ses identifiants de connexion avec des tiers non autorisés.",
          "Respecter la législation en vigueur en Polynésie française, notamment en matière fiscale et comptable.",
        ]} />
        <p>AERH ne saurait être tenu responsable des erreurs de saisie commises par l'Utilisateur, ni des conséquences fiscales ou comptables résultant d'une utilisation incorrecte du Service.</p>
      </Art>
      <Art titre="Article 7 — Obligations d'AERH">
        <p>AERH s'engage à :</p>
        <UL items={[
          "Fournir un accès au Service avec un taux de disponibilité cible de 99% (hors maintenance planifiée).",
          "Assurer la sauvegarde régulière des données de l'Utilisateur.",
          "Maintenir la confidentialité des données conformément à la Politique de confidentialité.",
          "Notifier l'Utilisateur de toute interruption planifiée avec un préavis raisonnable.",
          "Répondre aux demandes de support dans un délai de 48 heures ouvrées.",
        ]} />
      </Art>
      <Art titre="Article 8 — Propriété intellectuelle">
        <p>Le Service, son code source, son interface, sa documentation et l'ensemble de ses composants sont la propriété exclusive de la SARL Groupe AERH Polynésie et sont protégés par le droit applicable en Polynésie française. L'Abonnement confère à l'Utilisateur un droit d'accès personnel, non exclusif, non transférable et limité à la durée de l'Abonnement. Tout autre usage est strictement interdit. Les données saisies par l'Utilisateur dans le Service restent sa propriété exclusive. AERH ne revendique aucun droit sur ces données.</p>
      </Art>
      <Art titre="Article 9 — Responsabilité et garanties">
        <p>Le Service est fourni en l'état. AERH ne garantit pas que le Service sera exempt d'erreurs ou d'interruptions. AERH ne saurait être tenu responsable des dommages indirects résultant de l'utilisation ou de l'impossibilité d'utiliser le Service, notamment la perte de données, la perte de chiffre d'affaires ou les sanctions fiscales. La responsabilité d'AERH est limitée, en tout état de cause, au montant des sommes effectivement versées par l'Utilisateur au cours des 12 mois précédant le fait générateur du dommage.</p>
      </Art>
      <Art titre="Article 10 — Droit applicable et juridiction">
        <p>Les présentes CGU/CGV sont soumises au droit applicable en Polynésie française. En cas de litige, les parties s'engagent à rechercher une solution amiable. À défaut, tout litige relatif à l'interprétation ou à l'exécution des présentes sera soumis à la compétence exclusive du Tribunal de Commerce de Papeete.</p>
      </Art>
      <Art titre="Article 11 — Modification des CGU/CGV">
        <p>AERH se réserve le droit de modifier les présentes CGU/CGV à tout moment. Les modifications entrent en vigueur 30 jours après leur notification à l'Utilisateur par email. L'utilisation continue du Service après ce délai vaut acceptation des nouvelles conditions.</p>
      </Art>
      <Art titre="Article 12 — Contact">
        <p>Pour toute question relative aux présentes CGU/CGV :</p>
        <Coords />
      </Art>
    </Doc>
  );
}
